import type { Vector3 } from "three";
import type { CylinderSurface } from "./cylinder-surface";
import type { PipeTraceSolver } from "./pipe-tracker";
import { withLodPipeQuery } from "./trace-lod-pipe";
import { CYLINDER_FIT_CONFIG } from "./cylinder-fit";

const INITIAL_REACH = 0.05;
const MAX_REACH = 2;

/**
 * @param cloud Shared source and loader; acquired nodes remain borrowed.
 * @param clicked User's world-space location seed.
 * @param options Current request lifetime and owned worker seed operation.
 * @returns A validated model from bounded real 3D evidence, or undefined.
 */
export function fitLodPipeSeed(
  cloud: Parameters<typeof withLodPipeQuery>[0],
  clicked: Vector3,
  options: {
    signal: AbortSignal;
    cancelled(): boolean;
    fit: NonNullable<PipeTraceSolver["seed"]>;
  },
): Promise<CylinderSurface | undefined> {
  return withLodPipeQuery(cloud, async (query) => {
    for (let reach = INITIAL_REACH; ; reach = Math.min(MAX_REACH, reach * 2)) {
      if (options.cancelled()) return;
      const evidence = await query(clicked, reach, options.signal);
      if (options.cancelled()) return;
      if (!evidence.complete) {
        throw new Error("Pipe seed data could not be completely loaded.");
      }
      const fitted =
        evidence.points.length >= CYLINDER_FIT_CONFIG.minimumSamples
          ? await options.fit(evidence, clicked, options.cancelled)
          : undefined;
      if (options.cancelled()) return;
      if (fitted || reach === MAX_REACH) return fitted;
    }
  });
}
